import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronRight } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification, NOTIFICATION } from '../NotificationSystem';
import {
    listCommunityBuilds,
    listCommunityTopCreators,
    toggleCommunityBuildLike,
} from '../../services/buildApi';
import { CommunityTopCreatorsRow } from './CommunityTopCreatorsRow';
import { DiscordCta } from './DiscordCta';
import { CompactBuildCard } from '../build/CompactBuildCard';
import './HomeCommunitySection.css';

const TXT_LOG_IN_TO_LIKE = {
    fr: 'Connecte-toi pour liker.',
    en: 'Log in to like.',
    de: 'Melde dich an, um zu liken.',
    es: 'Inicia sesión para dar like.',
    pt: 'Entre para curtir.',
};
const TXT_LIKE_FAILED = {
    fr: 'Echec du like.',
    en: 'Like failed.',
    de: 'Like fehlgeschlagen.',
    es: 'No se ha podido dar like.',
    pt: 'Não foi possível curtir.',
};

const TOP_CREATORS_FETCH_LIMIT = 50;
/** Enough for ~8 cols × 2 rows (desktop) or 2 cols × 4 rows (mobile). */
const POPULAR_BUILDS_LIMIT = 16;

const DESKTOP_ROWS = 2;
const MOBILE_ROWS = 4;
const MOBILE_MQ = '(max-width: 62.4375rem)';

const TXT = {
    title: {
        fr: 'Rejoins la commu Dofgen',
        en: 'Join the Dofgen community',
        de: 'Komm zur Dofgen Community',
        es: 'Únete a la comunidad Dofgen',
        pt: 'Entre na comunidade Dofgen',
    },
    topCreators: {
        fr: 'Top créateurs',
        en: 'Top Creators',
        de: 'Top Ersteller',
        es: 'Top creadores',
        pt: 'Top criadores',
    },
    popular: {
        fr: 'Stuffs populaires',
        en: 'Popular builds',
        de: 'Beliebte Builds',
        es: 'Builds populares',
        pt: 'Builds populares',
    },
    loading: {
        fr: 'Chargement...',
        en: 'Loading...',
        de: 'Wird geladen...',
        es: 'Cargando...',
        pt: 'Carregando...',
    },
    empty: {
        fr: 'Aucun stuff publié pour le moment.',
        en: 'No published builds yet.',
        de: 'Noch keine veröffentlichten Builds.',
        es: 'Aún no hay builds publicadas.',
        pt: 'Ainda não há builds publicadas.',
    },
    connectDiscord: {
        fr: 'Se connecter avec Discord',
        en: 'Connect with Discord',
        de: 'Mit Discord anmelden',
        es: 'Iniciar sesión con Discord',
        pt: 'Entrar com Discord',
    },
    communityPage: {
        fr: 'Page de la communauté',
        en: 'Community page',
        de: 'Community-Seite',
        es: 'Página de la comunidad',
        pt: 'Página da comunidade',
    },
};

function t(dict, lang) {
    return dict[lang] || dict.en;
}

function parseCssPx(raw, fallback) {
    if (!raw || raw === 'normal') return fallback;
    const part = String(raw).trim().split(/\s+/)[0];
    const n = parseFloat(part);
    if (Number.isNaN(n)) return fallback;
    if (part.endsWith('rem')) {
        const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        return n * rootPx;
    }
    return n;
}

function countCardsForRows(gridEl, rows) {
    if (!gridEl || rows < 1) return 0;
    const w = gridEl.clientWidth;
    if (w <= 0) return rows;
    const cs = getComputedStyle(gridEl);
    const gapPx = parseCssPx(cs.columnGap || cs.gap, 8);
    const minRaw = cs.getPropertyValue('--home-community-card-min').trim() || '14.5rem';
    const cardMin = parseCssPx(minRaw, 232);
    const cols = Math.max(1, Math.floor((w + gapPx) / (cardMin + gapPx)));
    return cols * rows;
}

export function HomeCommunitySection({ lang = 'fr' }) {
    const { isAuthenticated, startLogin } = useAuth();
    const location = useLocation();
    const { showNotification } = useNotification();
    const [creators, setCreators] = useState([]);
    const [builds, setBuilds] = useState([]);
    const [loadingBuilds, setLoadingBuilds] = useState(true);
    const [visibleCount, setVisibleCount] = useState(8);
    const gridRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        listCommunityTopCreators({ limit: TOP_CREATORS_FETCH_LIMIT }).then((data) => {
            if (cancelled) return;
            setCreators(Array.isArray(data?.results) ? data.results : []);
        }).catch(() => {
            if (!cancelled) setCreators([]);
        });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoadingBuilds(true);
        listCommunityBuilds({
            sort: 'likes',
            page: 1,
            page_size: POPULAR_BUILDS_LIMIT,
        }).then((data) => {
            if (cancelled) return;
            setBuilds(Array.isArray(data?.results) ? data.results : []);
        }).catch(() => {
            if (!cancelled) setBuilds([]);
        }).finally(() => {
            if (!cancelled) setLoadingBuilds(false);
        });
        return () => { cancelled = true; };
    }, []);

    const measureVisible = useCallback(() => {
        const el = gridRef.current;
        if (!el) return;
        const mobile = typeof window !== 'undefined'
            && window.matchMedia(MOBILE_MQ).matches;
        const rows = mobile ? MOBILE_ROWS : DESKTOP_ROWS;
        const next = countCardsForRows(el, rows);
        setVisibleCount((prev) => (prev === next ? prev : next));
    }, []);

    useLayoutEffect(() => {
        measureVisible();
        const el = gridRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => measureVisible());
        ro.observe(el);
        const mq = window.matchMedia(MOBILE_MQ);
        const onMq = () => measureVisible();
        if (mq.addEventListener) mq.addEventListener('change', onMq);
        else mq.addListener(onMq);
        return () => {
            ro.disconnect();
            if (mq.removeEventListener) mq.removeEventListener('change', onMq);
            else mq.removeListener(onMq);
        };
    }, [measureVisible, builds.length, loadingBuilds]);

    const handleLike = async (id, hadLiked) => {
        if (!isAuthenticated) {
            showNotification(TXT_LOG_IN_TO_LIKE[lang] || TXT_LOG_IN_TO_LIKE.en, NOTIFICATION.INFO);
            return;
        }
        setBuilds((prev) => prev.map((r) => (r.id === id ? {
            ...r,
            viewer: { ...(r.viewer || {}), hasLiked: !hadLiked },
            metrics: {
                ...(r.metrics || {}),
                likes: Math.max(0, Number(r?.metrics?.likes || 0) + (hadLiked ? -1 : 1)),
            },
        } : r)));
        try {
            const res = await toggleCommunityBuildLike(id);
            setBuilds((prev) => prev.map((r) => (r.id === id ? {
                ...r,
                viewer: { ...(r.viewer || {}), hasLiked: !!res.liked },
                metrics: { ...(r.metrics || {}), likes: Number(res.likes || 0) },
            } : r)));
        } catch {
            setBuilds((prev) => prev.map((r) => (r.id === id ? {
                ...r,
                viewer: { ...(r.viewer || {}), hasLiked: hadLiked },
                metrics: {
                    ...(r.metrics || {}),
                    likes: Math.max(0, Number(r?.metrics?.likes || 0) + (hadLiked ? 1 : -1)),
                },
            } : r)));
            showNotification(TXT_LIKE_FAILED[lang] || TXT_LIKE_FAILED.en, NOTIFICATION.ERROR);
        }
    };

    const communityHref = `/${lang}/community`;
    const creatorsHref = `/${lang}/community/creators`;
    const visibleBuilds = builds.slice(0, Math.max(1, visibleCount));

    return (
        <section className="home-community" aria-labelledby="home-community-title">
            <header className="home-community-header">
                <h2 id="home-community-title" className="home-community-title">
                    {t(TXT.title, lang)}
                </h2>
            </header>

            {/* Above the creators strip, not next to the actions row below: the
                "Connect with Discord" button down there is a login, and the two
                sitting side by side would read as the same thing. */}
            <DiscordCta lang={lang} src="home" />

            <CommunityTopCreatorsRow
                creators={creators}
                lang={lang}
                title={t(TXT.topCreators, lang)}
                titleHref={creatorsHref}
                titleAs="h3"
                mutedTitle
            />

            <div className="home-community-builds">
                <div className="home-community-builds-heading">
                    <Link to={`${communityHref}?sort=likes`} className="home-community-builds-title-link">
                        <h3 className="home-community-builds-title">
                            <span>{t(TXT.popular, lang)}</span>
                            <ChevronRight className="home-community-builds-title-chevron" fontSize="inherit" aria-hidden />
                        </h3>
                    </Link>
                </div>
                {loadingBuilds ? (
                    <p className="home-community-status">{t(TXT.loading, lang)}</p>
                ) : builds.length === 0 ? (
                    <p className="home-community-status">{t(TXT.empty, lang)}</p>
                ) : (
                    <div ref={gridRef} className="home-community-builds-grid">
                        {visibleBuilds.map((entry) => (
                            <div key={entry.id} className="home-community-builds-cell">
                                <CompactBuildCard
                                    entry={entry}
                                    lang={lang}
                                    onToggleLike={handleLike}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="home-community-actions">
                <button
                    type="button"
                    onClick={() => startLogin(`${location.pathname}${location.search}`)}
                >
                    {t(TXT.connectDiscord, lang)}
                </button>
                <Link to={communityHref}>
                    <button type="button" className="primary alt">
                        {t(TXT.communityPage, lang)}
                    </button>
                </Link>
            </div>
        </section>
    );
}
